import { clone, isNil } from 'lodash';
import { Quaternion, Vector3 } from 'three';
import { MachineHelper } from '../classes/machine.helper';
import { TrajHelper } from '../classes/trajectory.helper';
import { Orientation, PitcherHand } from '../enums/pitches.enums';
import { IBallChar } from '../interfaces/i-ball-char';
import { IMachineState } from '../interfaces/i-machine-state';
import {
  IBallState,
  IPitch,
  ITrajectory,
  ITrajektRefBreak,
} from '../interfaces/pitches';
import {
  IAziAltCoordinate,
  IOrientation,
  ISeamOrientation,
  ISpin,
  ISpinExt,
  ISurfacePoint,
  IVelocity,
} from '../interfaces/pitches/i-base';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import {
  BALL_RADIUS_FT,
  DEG_TO_RAD,
  FTPS_TO_MPH,
  getMagnitude,
  getRotationMatrix,
  HR_TO_DEG,
  HR_TO_MIN,
  METERS_TO_INCHES,
  RAD_PER_SEC_TO_RPM,
  RAD_RIGHT_ANGLE,
  RAD_TO_DEG,
} from './math.utilities';
import { MiscHelper } from './misc.helper';
import { PLATE_EDGE_FT } from './plate.helper';

// rowan's magic number
export const BASEBALL_DRAG_COEFF = 38 / 150 / 150;

export const ARC_2024_INSET_FT = 1.415;

export const MIN_SHOTS_TO_ROTATE = 1;
export interface IGetQuaternionInput extends ISeamOrientation {
  spin: ISpin;
}

export class BallHelper {
  /** proportion of spin (0-100) that remains after removing wy */
  private static _getEfficiency(spin: ISpin) {
    const safeSign = Math.sign(spin.wy) || 1;
    const absSpinRatio = Math.abs(spin.wy) / this.getNetSpin(spin);
    return safeSign * (100 - absSpinRatio * 100);
  }

  private static _getSpinAxis(spin: ISpin) {
    return Math.atan2(spin.wz, spin.wx) * RAD_TO_DEG;
  }

  static has3DSpin(value: Partial<ISpin>) {
    const values = [value.wx, value.wy, value.wz];
    return (
      // some legal number
      values.every((v) => v !== undefined && v !== null && !isNaN(v)) &&
      // not all 0s
      !(value.wx === 0 && value.wy === 0 && value.wz === 0)
    );
  }

  /** shorthand using getMagnitude with qw, qx, qy, qz */
  static getOrientationNorm(value: IOrientation) {
    return getMagnitude([value.qw, value.qx, value.qy, value.qz]);
  }

  /** result is in whatever units given to it */
  static getSpeed(value: IVelocity): number {
    return getMagnitude([value.vx, value.vy, value.vz]);
  }

  // result is in MPH
  static getSpeedFromBs(value: IBallState): number {
    try {
      return getMagnitude([value.vx, value.vy, value.vz]);
    } catch (e) {
      console.error('getSpeedFromBs', value, e);
      return 0;
    }
  }

  /** shorthand using getMagnitude with wx, wy, wz */
  static getNetSpin(value: ISpin): number {
    return getMagnitude([value.wx, value.wy, value.wz]);
  }

  static convertSpinExtToSpin(value: ISpinExt): ISpin {
    const trueSpin = value.wnet * Math.cos(value.gyro_angle * DEG_TO_RAD);

    return {
      wx: trueSpin * Math.cos(value.waxis * DEG_TO_RAD),
      wy: Math.sqrt(Math.pow(value.wnet, 2) - Math.pow(trueSpin, 2)),
      wz: trueSpin * Math.sin(value.waxis * DEG_TO_RAD),
    };
  }

  static convertSpinToSpinExt(value: ISpin): ISpinExt {
    return {
      wnet: this.getNetSpin(value),
      gyro_angle: this._getGyroAngle(value),
      waxis: this._getSpinAxis(value),
    };
  }

  private static _getGyroAngle(value: ISpin): number {
    const wnet = this.getNetSpin(value);
    const wy = value.wy;
    const trueSpin = Math.sqrt(Math.pow(wnet, 2) - Math.pow(wy, 2));
    const gyroAngle = Math.acos(trueSpin / wnet) * RAD_TO_DEG;
    return gyroAngle;
  }

  /** shorthand using getMagnitude with qw, qx, qy, qz */
  static getSafeOrientationNorm(value: IOrientation) {
    try {
      return this.getOrientationNorm(value);
    } catch {
      return undefined;
    }
  }

  /** shorthand using getMagnitude with vx, vy, vz */
  static getSafeSpeed(value: IVelocity): number | undefined {
    try {
      return this.getSpeed(value);
    } catch {
      return undefined;
    }
  }

  /** shorthand using getMagnitude with wx, wy, wz */
  static getSafeNetSpin(value: ISpin): number | undefined {
    try {
      return this.getNetSpin(value);
    } catch {
      return undefined;
    }
  }

  /** shorthand using px relative to 0 */
  static getPitcherHand(
    releasePx: number | null | undefined
  ): PitcherHand | undefined {
    try {
      if (isNil(releasePx)) {
        return undefined;
      }

      if (releasePx > 0) {
        return PitcherHand.LHP;
      }

      return PitcherHand.RHP;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  /** inverse of {getBallStateFromChars} */
  static getCharsFromPitch(pitch: IPitch): IBallChar {
    const bs = pitch.bs;
    const seams = pitch.seams ?? this.getSeamsFromBS(bs);
    const spinExt = this.convertSpinToSpinExt(bs);

    const output: IBallChar = {
      _id: pitch._id,

      speed: this.getSpeedFromBs(bs),
      orientation: Orientation.CUS,

      px: bs.px,
      py: bs.py,
      pz: bs.pz,
      wx: bs.wx,
      wy: bs.wy,
      wz: bs.wz,

      wnet: spinExt.wnet,
      gyro_angle: spinExt.gyro_angle,
      waxis: spinExt.waxis,

      breaks: this.getSafePitchBreaks(pitch),

      latitude_deg: seams.latitude_deg,
      longitude_deg: seams.longitude_deg,
    };

    return output;
  }

  // in trajekt frame of ref
  static getSafePitchBreaks(
    pitch: Partial<IPitch>
  ): ITrajektRefBreak | undefined {
    if (pitch.breaks) {
      return pitch.breaks;
    }

    if (!pitch.traj) {
      return undefined;
    }

    const trajBreaks = TrajHelper.getBreaks(pitch.traj);

    if (!isNaN(trajBreaks.xInches) && !isNaN(trajBreaks.zInches)) {
      return trajBreaks;
    }

    return undefined;
  }

  /** inverse of {getCharsFromBallState} */
  static getBallStateFromChars(ball: Partial<IBallChar>): IBallState {
    if (isNil(ball.wx)) {
      ball.wx = 0;
    }

    if (isNil(ball.wy)) {
      ball.wy = 0;
    }

    if (isNil(ball.wz)) {
      ball.wz = 0;
    }

    if (isNil(ball.px)) {
      ball.px = 0;
    }

    if (isNil(ball.py)) {
      ball.py = 0;
    }

    if (isNil(ball.pz)) {
      ball.pz = 0;
    }

    if (ball.wx === 0 && ball.wy === 0 && ball.wz === 0) {
      /** fix to avoid math weirdness */
      ball.wx = 0.1;
    }

    const vel: IVelocity = {
      vx: 0,
      vy: -(ball.speed ?? 0),
      vz: 0,
    };

    const quat = this.getQuatFromSeams({
      latitude_deg: ball.latitude_deg ?? 0,
      longitude_deg: ball.longitude_deg ?? 0,
      spin: {
        wx: ball.wx,
        wy: ball.wy,
        wz: ball.wz,
      },
    });

    const output: IBallState = {
      ...quat,

      px: ball.px,
      py: ball.py,
      pz: ball.pz,

      ...vel,

      vnet: getMagnitude([vel.vx, vel.vy, vel.vz]),

      wx: ball.wx,
      wy: ball.wy,
      wz: ball.wz,

      wnet: getMagnitude([ball.wx, ball.wy, ball.wz]),
    };

    return output;
  }

  static safeNormalize(vector: Vector3, minNonZero = 1e-9): Vector3 {
    if (vector.length() >= minNonZero) {
      return vector.normalize();
    }

    const clone = new Vector3(minNonZero, vector.y, vector.z);
    return clone.normalize();
  }

  static getPitchSeams(pitch: IPitch): ISeamOrientation {
    if (pitch.seams) {
      return pitch.seams;
    }

    return this.getSeamsFromBS(pitch.bs);
  }

  // todo: determine which of Rapsodo vs OD gives better results and substitute BS method
  static getShotSeams(shot: IMachineShot): ISeamOrientation | undefined {
    if (shot.seams) {
      return shot.seams;
    }

    if (!shot.bs) {
      return undefined;
    }

    // fallback to ball state (if possible)
    return this.getSeamsFromBS(shot.bs);
  }

  static getSeamsFromBS(bs: IBallState): ISeamOrientation {
    const spin: ISpin = {
      wx: bs.wx,
      wy: bs.wy,
      wz: bs.wz,
    };
    const spinAxis = this.safeNormalize(new Vector3(spin.wx, spin.wy, spin.wz));
    const quat = new Quaternion(bs.qx, bs.qy, bs.qz, bs.qw);
    const invQuat = quat.invert();
    const ptViaQuat: ISurfacePoint = spinAxis.applyQuaternion(invQuat);

    const seams = this.convertPointToSeams(ptViaQuat);

    return seams;
  }

  // converts from Trajekt (x,y,z) to Hawkeye (x,z,-y)
  static convertPoint_T2H(point: ISurfacePoint): ISurfacePoint {
    const output: ISurfacePoint = {
      x: point.x,
      y: point.z,
      z: -point.y,
    };

    return output;
  }

  // converts from Hawkeye (x,y,z) to Trajekt (x,-z,y)
  static convertPoint_H2T(point: ISurfacePoint): ISurfacePoint {
    const output: ISurfacePoint = {
      x: point.x,
      y: -point.z,
      z: point.y,
    };

    return output;
  }

  static convertPointToSeams(point: ISurfacePoint): ISeamOrientation {
    const output: ISeamOrientation = {
      latitude_deg: (RAD_RIGHT_ANGLE - Math.acos(point.z)) * RAD_TO_DEG,
      longitude_deg: Math.atan2(point.x, -point.y) * RAD_TO_DEG,
    };

    return output;
  }

  static convertSeamsToPoint(seam: ISeamOrientation): ISurfacePoint {
    const z = Math.cos(RAD_RIGHT_ANGLE - seam.latitude_deg * DEG_TO_RAD);
    const zFactor = Math.sqrt(1 - Math.pow(z, 2));

    const output: ISurfacePoint = {
      x: Math.sin(seam.longitude_deg * DEG_TO_RAD) * zFactor,
      y: -Math.cos(seam.longitude_deg * DEG_TO_RAD) * zFactor,
      z: z,
    };

    return output;
  }

  static getAverageSeams(
    pitch: IPitch,
    seams: ISeamOrientation[]
  ): ISeamOrientation {
    const pitchSeams = this.getPitchSeams(pitch);
    const pitchPt = this.convertSeamsToPoint(pitchSeams);

    const closestShotPts = seams.map((shotSeams) => {
      const shotPt = this.convertSeamsToPoint(shotSeams);

      // ascending 3D cartesian distance from pitchPt
      const equivPts = this.getEquivalentPoints(shotPt);

      const logs = equivPts.map((pt) => {
        const seams = this.convertPointToSeams(pt);

        return {
          ref_point: pitchPt,
          point: pt,
          point_distance: this.getPointDistance(pitchPt, pt),

          ref_seams: pitchSeams,
          seams: seams,
          seams_distance: this.getSeamsDistance(pitchSeams, seams),
        };
      });

      equivPts.sort((a, b) => {
        // delta between surface points
        const distA = this.getPointDistance(pitchPt, a);
        const distB = this.getPointDistance(pitchPt, b);

        if (distA !== distB) {
          return distA < distB ? -1 : 1;
        }

        // delta between seams degree values as tie breaker
        const seamA = this.convertPointToSeams(a);
        const seamB = this.convertPointToSeams(b);

        const sDistA = this.getSeamsDistance(pitchSeams, seamA);
        const sDistB = this.getSeamsDistance(pitchSeams, seamB);

        return sDistA < sDistB ? -1 : 1;
      });

      return equivPts[0];
    });

    const avgPt: ISurfacePoint = MiscHelper.getMeanObject(
      closestShotPts
    ) as ISurfacePoint;

    const unitAvgPt = this.getUnitPoint(avgPt);
    return this.convertPointToSeams(unitAvgPt);
  }

  static getUnitPoint(point: ISurfacePoint): ISurfacePoint {
    const magnitude = this.getPointDistance(point);

    if (magnitude === 0) {
      return {
        x: 0,
        y: 0,
        z: 0,
      };
    }

    const output: ISurfacePoint = {
      x: point.x / magnitude,
      y: point.y / magnitude,
      z: point.z / magnitude,
    };

    return output;
  }

  // second point is optional (origin by default)
  static getPointDistance(
    p1: ISurfacePoint,
    p2: ISurfacePoint = { x: 0, y: 0, z: 0 }
  ): number {
    const diffs = [p1.x - p2.x, p1.y - p2.y, p1.z - p2.z];
    const squares = diffs.map((d) => Math.pow(d, 2));
    const sum = squares.reduce((total, current) => total + current, 0);
    return Math.sqrt(sum);
  }

  // second seams is optional (origin by default)
  static getSeamsDistance(
    s1: ISeamOrientation,
    s2: ISeamOrientation = { latitude_deg: 0, longitude_deg: 0 }
  ): number {
    const diffs = [
      s1.latitude_deg - s2.latitude_deg,
      s1.longitude_deg - s2.longitude_deg,
    ];
    const squares = diffs.map((d) => Math.pow(d, 2));
    const sum = squares.reduce((total, current) => total + current, 0);
    return Math.sqrt(sum);
  }

  static getEquivalentPoints(point: ISurfacePoint): ISurfacePoint[] {
    return [
      {
        // eq0, aka: original
        x: point.x,
        y: point.y,
        z: point.z,
      },
      {
        // eq1
        x: -point.x,
        y: point.y,
        z: -point.z,
      },
      {
        // eq2
        x: point.z,
        y: -point.y,
        z: point.x,
      },
      {
        // eq3
        x: -point.z,
        y: -point.y,
        z: -point.x,
      },
    ];
  }

  // Generates an arbitrary quaternion which fully defines the ball's release orientation such that
  // the input latitude_deg and longitude_deg are conserved.
  static getQuatFromSeams(input: IGetQuaternionInput): IOrientation {
    // Get surface point and spin axis as vectors
    const surfacePoint = this.convertSeamsToPoint(input);
    const surfacePointVec = new Vector3(
      surfacePoint.x,
      surfacePoint.y,
      surfacePoint.z
    );
    const spinAxisVec = this.safeNormalize(
      new Vector3(input.spin.wx, input.spin.wy, input.spin.wz)
    );
    // Compute angle and axis which separate the two unit vectors
    const theta = Math.acos(surfacePointVec.dot(spinAxisVec));
    const axis = this.safeNormalize(surfacePointVec.cross(spinAxisVec));
    // Convert axis/angle to quaternion
    return {
      qw: Math.cos(theta / 2),
      qx: Math.sin(theta / 2) * axis.x,
      qy: Math.sin(theta / 2) * axis.y,
      qz: Math.sin(theta / 2) * axis.z,
    };
  }

  static getLocalSpin(globalSpin: ISpin, speed: IVelocity): ISpin {
    const xSq = Math.pow(speed.vx, 2);
    const ySq = Math.pow(speed.vy, 2);
    const zSq = Math.pow(speed.vz, 2);

    const invRot: IAziAltCoordinate = {
      azimuth_rad: -Math.asin(speed.vx / Math.sqrt(xSq + ySq)),
      altitude_rad: -Math.asin(speed.vz / Math.sqrt(xSq + ySq + zSq)),
    };

    const mx = getRotationMatrix({
      z_rad: invRot.azimuth_rad,
      x_rad: -invRot.altitude_rad,
    });

    const localSpin = new Vector3(
      globalSpin.wx,
      globalSpin.wy,
      globalSpin.wz
    ).applyMatrix3(mx);

    return {
      wx: localSpin.x,
      wy: localSpin.y,
      wz: localSpin.z,
    };
  }

  static getGlobalSpin(localSpin: ISpin, speed: IVelocity): ISpin {
    const xSq = Math.pow(speed.vx, 2);
    const ySq = Math.pow(speed.vy, 2);
    const zSq = Math.pow(speed.vz, 2);

    const invRot: IAziAltCoordinate = {
      azimuth_rad: -Math.asin(speed.vx / Math.sqrt(xSq + ySq)),
      altitude_rad: -Math.asin(speed.vz / Math.sqrt(xSq + ySq + zSq)),
    };

    const mx = getRotationMatrix({
      z_rad: invRot.azimuth_rad,
      x_rad: -invRot.altitude_rad,
    });

    // transpose matrix to invert rotation
    const mx_transposed = mx.transpose();

    const globalSpin = new Vector3(
      localSpin.wx,
      localSpin.wy,
      localSpin.wz
    ).applyMatrix3(mx_transposed);

    return {
      wx: globalSpin.x,
      wy: globalSpin.y,
      wz: globalSpin.z,
    };
  }

  // value should look like '12:15' or '1:23'
  static convertTimeToDegrees(value: string) {
    try {
      const hhmm = value.split(':').map((s) => parseFloat(s));

      if (hhmm.length !== 2) {
        return 0;
      }

      const hrs = hhmm[0] + hhmm[1] / HR_TO_MIN;
      return hrs * HR_TO_DEG;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  // in trajekt frame of ref
  static getSafeShotBreaks(
    shot: Partial<IMachineShot>
  ): ITrajektRefBreak | undefined {
    if (shot.break) {
      return {
        // rapsodo's frame of ref is opposite to trajekt's
        xInches: -shot.break.PITCH_HBTrajectory * METERS_TO_INCHES,
        zInches: shot.break.PITCH_VBTrajectory * METERS_TO_INCHES,
      };
    }

    if (!shot.traj) {
      return;
    }

    const trajBreaks = TrajHelper.getBreaks(shot.traj);

    if (!isNaN(trajBreaks.xInches) && !isNaN(trajBreaks.zInches)) {
      return trajBreaks;
    }

    return;
  }

  static getMsWithQuatFromSeams(config: {
    ms: IMachineState;
    seams: ISeamOrientation;
    spin: ISpin;
  }) {
    const adjOrientation = BallHelper.getQuatFromSeams({
      ...config.seams,
      spin: config.spin,
    });

    const output = clone(config.ms);

    output.qw = adjOrientation.qw;
    output.qx = adjOrientation.qx;
    output.qy = adjOrientation.qy;
    output.qz = adjOrientation.qz;

    output.full_hash = MachineHelper.getMSHash('full', output);
    output.matching_hash = MachineHelper.getMSHash('matching', output);

    return output;
  }

  // for building traj > bs for break priority pitches
  static getBsFromTraj(
    traj: ITrajectory,
    seams: ISeamOrientation,
    wy: number // aka: omega y
  ): IBallState {
    const vnetFTPS = getMagnitude([traj.vx, traj.vy, traj.vz]);

    // in FTPS squared
    const localAccel = TrajHelper.getLocalAccelWithoutGravity(traj);

    // specifically to the front of the plate for breaks
    const tofSec = TrajHelper.getFlightTimeSec(traj, PLATE_EDGE_FT);

    const breakX = 0.5 * localAccel.ax * Math.pow(tofSec, 2);
    const breakZ = 0.5 * localAccel.az * Math.pow(tofSec, 2);
    const sRX = (0.75 * Math.abs(breakX)) / (10 - 1.25 * Math.abs(breakX));
    const sRZ = (0.75 * Math.abs(breakZ)) / (10 - 1.25 * Math.abs(breakZ));
    const wx =
      (-Math.sign(breakZ) * sRZ * RAD_PER_SEC_TO_RPM * vnetFTPS) /
      BALL_RADIUS_FT;
    const wz =
      (Math.sign(breakX) * sRX * RAD_PER_SEC_TO_RPM * vnetFTPS) /
      BALL_RADIUS_FT;

    const quat = BallHelper.getQuatFromSeams({
      latitude_deg: seams.latitude_deg,
      longitude_deg: seams.longitude_deg,
      spin: {
        wx: wx,
        wy: wy,
        wz: wz,
      },
    });

    const output: IBallState = {
      px: traj.px,
      py: traj.py,
      pz: traj.pz,
      vx: traj.vx * FTPS_TO_MPH,
      vy: traj.vy * FTPS_TO_MPH,
      vz: traj.vz * FTPS_TO_MPH,
      vnet: getMagnitude([traj.vx, traj.vy, traj.vz]) * FTPS_TO_MPH,
      wnet: getMagnitude([wx, wy, wz]),
      wx: wx,
      wy: wy,
      wz: wz,
      qw: quat.qw,
      qx: quat.qx,
      qy: quat.qy,
      qz: quat.qz,
    };

    return output;
  }
}
